import { GetIntegrationMetricName } from 'components/admin/forms/metricPicker';
import { Box, DomHelpers, Forms, Help, Loading } from '@salesperformanceint/e4shared';
import React from 'react';
import { useSelector } from 'react-redux';
import MetricsTable from '../metricsTable';
import PerformanceCompetencyCard from '../behaviours/components/performanceCompetencyCard';
import CRMGoals from '../../../crmGoals';
import Motivators from '../motivators';
import posthog from 'posthog-js';

export const Metrics = ({ profile, text, show, onFlag, data, loading, reloading }) => {
    const [tab, setTab] = React.useState(false);
    const [goalTab, setGoalTab] = React.useState('goals');
    const [tabs, setTabs] = React.useState([]);
    const [metrics, setMetrics] = React.useState([]);
    const [metricId, setMetricId] = React.useState(0);
    const [metricType, setMetricType] = React.useState('');
    const [competencies, setCompetencies] = React.useState([]);
    const [ratedData, setRatedData] = React.useState([]);

    const { user } = useSelector(state => state.user);

    React.useEffect(() => {
        const resizeHandler = () => DomHelpers.arrangeTiles('behaviour-box');
        setTimeout(() => resizeHandler(), 250);
        window.addEventListener('resize', resizeHandler);

        return () => {
            window.removeEventListener('resize', resizeHandler);
        };
    }, [tab, loading]);

    React.useEffect(() => {
        const resizeHandler = () => {
            DomHelpers.arrangeTiles('box-match');
            DomHelpers.arrangeTiles('behaviour-sync');
        };

        setTimeout(() => resizeHandler(), 250);
        window.addEventListener('resize', resizeHandler);

        return () => {
            window.removeEventListener('resize', resizeHandler);
        };
    }, [loading, show, metricId, tab]);

    React.useEffect(() => {
        if (data?.length) {
            setRatedData(data.filter(x => x.rating));
        } else {
            setRatedData([]);
        }
    }, [data]);

    React.useEffect(() => {
        const mapped = data
            .reduce((a, c) => a.concat(c.metrics), [])
            .map(d => ({ ...d, ref: d.type === 'Integration' ? d.ref : 'CRM', key: `${d.type}-${d.id}` }));
        const temp = [...new Map(mapped.map(x => [x['key'], x])).values()];
        setMetrics(temp);
    }, [data]);

    React.useEffect(() => {
        let temp = [];

        if (!loading && !reloading) {
            temp = temp.concat(
                metrics.reduce((a, c) => {
                    const ref = c.type === 'CRM' || c.type === 'RealTime' ? 'CRM' : c.ref;
                    return a.some(x => x === ref) ? a : a.concat([ref]);
                }, [])
            );

            const hasIntegration = (user, ref) => {
                return user?.integrations && user.integrations.some(i => i.ref === ref);
            };

            setTabs(
                temp
                    .filter(x => x === 'CRM')
                    .concat(temp.filter(x => x !== 'CRM' && x !== 'e4').sort((a, b) => (a > b ? 1 : -1)))
                    .concat(temp.filter(x => x === 'e4'))
                    .filter(x => (x === 'CRM' && user.crmIntegration) || hasIntegration(user, x))
            );

            !tab && setTab(temp[0]);
        }
    }, [metrics]);

    React.useEffect(() => {
        const temp = data
            .filter(b => b.metrics.some(m => m.type === metricType && m.id === metricId))
            .sort((a, b) => (a.name > b.name ? 1 : -1));

        setCompetencies([...temp]);
    }, [data, tab, metricId]);

    const tabNames = tab => {
        const integration = tab === 'CRM' ? user.crmIntegration : tab;
        return (
            <span title={GetIntegrationMetricName({ ref: tab }, user)}>
                <img className="tab-icon" src={`/dist/img/${integration.toLowerCase()}-icon.png`} loading="lazy" alt="" />
            </span>
        );
    };

    return (
        <>
            <div className="flex flex-row flex-center">
                <h4 className="">{text.metrics.title}</h4>
                <Help id="metric-tooltip" className="flex-grow">
                    {text.metrics.tooltip}
                </Help>
            </div>
            {loading ? (
                <Loading />
            ) : (
                <div className="performance-metrics">
                    <div className="row">
                        <div className="col-md-8 metric-table-wrapper">
                            <Box
                                tab={tab || 'one'}
                                setTab={x => {
                                    setTab(x);
                                    posthog.capture('performance-dashboard-set-metric-tab', { tab: x });
                                }}
                                tabs={tabs}
                                tabNames={tabNames}
                                className="box-match"
                            >
                                {metrics?.length > 0 ? (
                                    <span className="metric-table">
                                        <MetricsTable
                                            metrics={metrics
                                                .filter(m => m.ref === tab)
                                                .sort((a, b) => (a.sequence > b.sequence ? 1 : -1))}
                                            id={`metrics-${tab}`}
                                            text={text}
                                            reloading={reloading}
                                            show={show}
                                            onClick={(id, type) => {
                                                setMetricId(id);
                                                setMetricType(type);
                                            }}
                                            hideIcon
                                        />
                                    </span>
                                ) : (
                                    <div className="metric-no-results">
                                        <span>{ratedData.length > 0 ? text.metrics.noMetrics : text.metrics.noResults}</span>
                                    </div>
                                )}
                            </Box>
                        </div>
                        <div className="col-md-4">
                            <Box
                                tab={goalTab || 'goals'}
                                setTab={x => {
                                    setGoalTab(x);
                                    posthog.capture('performance-dashboard-set-motivators-tab', { tab: x });
                                }}
                                tabs={['goals', 'motivators']}
                                tabNames={{ goals: text.metrics.tabs.goals, motivators: text.metrics.tabs.motivators }}
                                className="box-match"
                            >
                                {goalTab === 'goals' && (
                                    <div className="metric-tab-goals">
                                        <CRMGoals
                                            profile={profile}
                                            showPaging={false}
                                            showNavigation
                                            inBox={false}
                                            breakAt={400}
                                        />
                                    </div>
                                )}
                                {goalTab === 'motivators' && <Motivators profile={profile} text={text} />}
                            </Box>
                        </div>
                    </div>
                    {competencies.length > 0 && (
                        <div className="">
                            <h4 className="flex-grow">{text.metrics.behaviours}</h4>
                            <div className="row">
                                {competencies.map(competency => (
                                    <div key={`behaviour-${competency.id}`} className="col-sm-6 col-md-4">
                                        <PerformanceCompetencyCard
                                            text={text}
                                            profile={profile}
                                            competency={competency}
                                            reloading={reloading}
                                            onFlag={onFlag}
                                            show={show}
                                            basic
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

export default Metrics;
